import { AxiosInstance } from 'axios';

import { PAYMENT_ORDER_API_PATHS } from '../constants';
import {
  CreateRechargeOrderReq,
  CreateRechargeOrderResp,
  CreateVirtualProductOrderReq,
  CreateVirtualProductOrderResp,
  GetOrdersReq,
  GetOrdersResp,
  GetPurchasePreviewReq,
  GetPurchasePreviewResp,
  InitiatePaymentReq,
  InitiatePaymentResp,
  OrderStatusResponse,
  PaymentOrderApiResponse,
  PaymentOrderType,
  ProductItem,
} from '../types';
import {
  GetCreditHistoryReq,
  GetCreditHistoryResp,
} from '../types/credit-history';
import {
  FallbackPaymentProvider,
  GetFallbackPaymentProvidersResp,
} from '../types/fallback-payment-providers';
import { GetPendingPaymentsNotificationResp } from '../types/notifications';
import {
  GetProductPurchasesReq,
  GetProductPurchasesResp,
} from '../types/product-purchases';

export const createVirtualProductOrder = (
  axios: AxiosInstance,
  req: CreateVirtualProductOrderReq
): Promise<CreateVirtualProductOrderResp> =>
  axios
    .post<PaymentOrderApiResponse<CreateVirtualProductOrderResp>>(
      PAYMENT_ORDER_API_PATHS.CREATE_VIRTUAL_PRODUCT_ORDER,
      req
    )
    .then(r => r.data.data as CreateVirtualProductOrderResp);

export const createRechargeOrder = (
  axios: AxiosInstance,
  req: CreateRechargeOrderReq
): Promise<CreateRechargeOrderResp> =>
  axios
    .post<PaymentOrderApiResponse<CreateRechargeOrderResp>>(
      PAYMENT_ORDER_API_PATHS.CREATE_RECHARGE_ORDER,
      req
    )
    .then(r => r.data.data as CreateRechargeOrderResp);

export const initiatePayment = (
  axios: AxiosInstance,
  req: InitiatePaymentReq
): Promise<InitiatePaymentResp> =>
  axios
    .post<PaymentOrderApiResponse<InitiatePaymentResp>>(
      PAYMENT_ORDER_API_PATHS.INITIATE_PAYMENT,
      req
    )
    .then(r => r.data.data as InitiatePaymentResp);

export const getOrderStatus = (
  axios: AxiosInstance,
  orderNumber: string,
  orderType: PaymentOrderType
): Promise<OrderStatusResponse> =>
  axios
    .get<PaymentOrderApiResponse<OrderStatusResponse>>(
      PAYMENT_ORDER_API_PATHS.GET_ORDER_STATUS(orderNumber),
      {
        params: { orderType },
      }
    )
    .then(r => r.data.data as OrderStatusResponse);

export const getFallbackPaymentProviders = (
  axios: AxiosInstance,
  orderNumber: string
): Promise<FallbackPaymentProvider[]> =>
  axios
    .get<PaymentOrderApiResponse<GetFallbackPaymentProvidersResp>>(
      PAYMENT_ORDER_API_PATHS.GET_FALLBACK_PAYMENT_PROVIDERS(orderNumber)
    )
    .then(r => r.data.data?.fallbackPaymentProviders ?? []);

export const cancelVirtualProductOrder = (
  axios: AxiosInstance,
  orderNumber: string,
  companyId: string,
  orderType: PaymentOrderType
): Promise<void> =>
  axios
    .post(
      PAYMENT_ORDER_API_PATHS.CANCEL_VIRTUAL_PRODUCT_ORDER(orderNumber),
      null,
      {
        params: { companyId, orderType },
      }
    )
    .then(() => undefined);

export const cancelSubscription = (
  axios: AxiosInstance,
  req: { orderNumber: string; companyId: string }
): Promise<void> =>
  axios
    .post(PAYMENT_ORDER_API_PATHS.CANCEL_SUBSCRIPTION, req)
    .then(() => undefined);
export const getProducts = (
  axios: AxiosInstance,
  req: { type: string; companyId: string }
): Promise<ProductItem[]> =>
  axios
    .get<PaymentOrderApiResponse<{ products: ProductItem[] }>>(
      PAYMENT_ORDER_API_PATHS.GET_PRODUCTS,
      { params: req }
    )
    .then(r => r.data.data?.products ?? []);

export const getPurchasePreview = (
  axios: AxiosInstance,
  req: GetPurchasePreviewReq
): Promise<GetPurchasePreviewResp> =>
  axios
    .get<PaymentOrderApiResponse<GetPurchasePreviewResp>>(
      PAYMENT_ORDER_API_PATHS.GET_PURCHASE_PREVIEW,
      { params: req }
    )
    .then(r => r.data.data as GetPurchasePreviewResp);

export const getOrders = (
  axios: AxiosInstance,
  req: GetOrdersReq
): Promise<GetOrdersResp> =>
  axios
    .get<PaymentOrderApiResponse<GetOrdersResp>>(
      PAYMENT_ORDER_API_PATHS.GET_ORDERS,
      { params: req }
    )
    .then(r => r.data.data as GetOrdersResp);

export const getCreditHistory = (
  axios: AxiosInstance,
  req: GetCreditHistoryReq
): Promise<GetCreditHistoryResp> =>
  axios
    .get<PaymentOrderApiResponse<GetCreditHistoryResp>>(
      PAYMENT_ORDER_API_PATHS.GET_CREDIT_HISTORY,
      {
        params: {
          companyId: req.companyId,
          cursor: req.cursor,
          pageSize: req.pageSize,
          usages: req.usages?.join(','),
          creatorIds: req.creatorIds?.join(','),
          sort: req.sort,
        },
      }
    )
    .then(r => r.data.data as GetCreditHistoryResp);

export const getProductPurchases = (
  axios: AxiosInstance,
  req: GetProductPurchasesReq
): Promise<GetProductPurchasesResp> =>
  axios
    .get<PaymentOrderApiResponse<GetProductPurchasesResp>>(
      PAYMENT_ORDER_API_PATHS.GET_PRODUCT_PURCHASES,
      {
        params: req,
      }
    )
    .then(r => r.data.data as GetProductPurchasesResp);

export const getPendingPaymentsNotification = (
  axios: AxiosInstance,
  req: { companyId: string }
): Promise<GetPendingPaymentsNotificationResp> =>
  axios
    .get<PaymentOrderApiResponse<GetPendingPaymentsNotificationResp>>(
      PAYMENT_ORDER_API_PATHS.GET_PENDING_PAYMENTS_NOTIFICATION,
      { params: req }
    )
    .then(r => r.data.data as GetPendingPaymentsNotificationResp);
